/**
 * L'équipe du tenant — chunk 9.AE.1.
 *
 * Ferme `D-NEW-WEB-NO-USERS-SCREEN` et `D-NEW-WEB-BROKERS-READ-ONLY`.
 *
 * ## Un seul modèle d'utilisateur
 *
 * Un courtier **est** un utilisateur : `app.broker_profiles` a `user_id` en clé
 * primaire et en clé étrangère. Il n'y a donc pas deux populations à réconcilier —
 * `/v1/users` liste les membres, `/v1/brokers` expose ceux qui ont un profil de
 * courtier, et les deux vues portent sur les mêmes lignes.
 *
 * C'est pourquoi ce module unique sert les deux surfaces : les séparer aurait donné
 * deux caches, deux invalidations, et un écran où désactiver un courtier ne le
 * ferait pas disparaître de la liste des utilisateurs.
 *
 * ## Aucune règle d'autorisation ici
 *
 * Les gardes vivent côté serveur : `USERS_USER_AUTH_REQUIRED`,
 * `USERS_ROLE_ESCALATION_FORBIDDEN`, `USERS_SELF_ROLE_CHANGE_FORBIDDEN`,
 * `USERS_SELF_DELETE_FORBIDDEN` (9.Y.1). L'interface les reflète pour éviter au
 * conseiller un aller-retour inutile, elle ne les remplace pas — un bouton masqué
 * n'a jamais protégé personne.
 */

import type { Schemas } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";

import { api } from "../api";

export type TeamRole = "super_admin" | "tenant_admin" | "broker" | "client_contact";
export type TeamStatus = "active" | "pending" | "disabled";

export type TeamMember = {
  id: string;
  email: string;
  full_name: string | null;
  role: TeamRole;
  status: TeamStatus;
  phone: string | null;
  locale: string | null;
  last_login_at: string | null;
  created_at: string;
  accepted_at?: string | null;
  invited_at?: string | null;
  invitation_history?: z.infer<typeof Schemas.MemberInvitationHistorySchema> | null;
};

export type BrokerProfile = {
  user_id: string;
  license_number: string | null;
  license_expiry: string | null;
  specialties: string[] | null;
  bio: string | null;
};

type Page<T> = { items: T[]; next_cursor: string | null };

/**
 * Les membres du tenant.
 *
 * `role=all` : la valeur par défaut du filtre serveur est `non_broker`, ce qui
 * exclurait précisément les courtiers — l'essentiel d'un cabinet. Un écran
 * « Équipe » qui cache les courtiers ne montre pas l'équipe.
 */
export function useTeamMembers(params: { status?: string; q?: string } = {}) {
  const sp = new URLSearchParams({ role: "all", limit: "100" });
  if (params.status && params.status !== "all") sp.set("status", params.status);
  if (params.q) sp.set("q", params.q);
  return useQuery({
    queryKey: ["team", "members", params],
    queryFn: () => api.get<Page<TeamMember>>(`/v1/users?${sp.toString()}`),
    placeholderData: (prev) => prev,
  });
}

/** Les profils de courtier, pour enrichir la liste sans deuxième source de vérité. */
export function useBrokerProfiles() {
  return useQuery({
    queryKey: ["team", "brokers"],
    queryFn: () =>
      api.get<Page<TeamMember & { profile?: BrokerProfile | null }>>("/v1/brokers?limit=100"),
    placeholderData: (prev) => prev,
  });
}

function useInvalidateTeam() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["team"] });
    // La liste des courtiers alimente aussi les sélecteurs de responsable ailleurs
    // dans le produit : les laisser périmés afficherait un courtier désactivé.
    void qc.invalidateQueries({ queryKey: ["brokers"] });
  };
}

export type InviteMemberInput = {
  email: string;
  full_name: string;
  role: TeamRole;
  locale: string;
  phone?: string;
  message?: string;
};

/**
 * `POST /v1/users/invite` — le mécanisme d'authentification existant.
 *
 * Aucun mot de passe n'est créé à la main : le serveur délègue à `inviteUser()`, qui
 * gère les trois modes d'invitation selon `INVITE_EMAIL_MODE` et renvoie l'invité
 * vers `/auth/accept-invite`.
 */
export function useInviteMember() {
  const invalider = useInvalidateTeam();
  return useMutation({
    mutationFn: (input: InviteMemberInput) => api.post<TeamMember>("/v1/users/invite", input),
    onSuccess: invalider,
  });
}

/** Ce que la route de renvoi confirme à l'appelant. */
export type ResendInviteResult = {
  email_queued: boolean;
  /** Une invitation attendait DÉJÀ d'être expédiée — ce n'est pas une erreur. */
  already_queued: boolean;
  outbox_id?: string;
};

/**
 * `POST /v1/users/:id/resend-invite` — chunk 9.AV.1.
 *
 * Route **dédiée**, et non `POST /v1/users/invite` : celui-ci avait historiquement une sémantique
 * d'UPSERT qui réécrit rôle, langue et nom avec ce que le dialogue de création
 * contient. Renvoyer une invitation à un administrateur de cabinet par ce chemin
 * l'aurait rétrogradé en silence — le champ rôle du dialogue s'initialise à
 * `broker`.
 *
 * Celle-ci ne fait que refrapper le lien et le mettre en file. Elle n'écrit rien
 * sur le membre.
 */
export function useResendInvite() {
  const invalider = useInvalidateTeam();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<ResendInviteResult>(`/v1/users/${userId}/resend-invite`, {}),
    onSuccess: invalider,
  });
}

/**
 * `PATCH /v1/users/:id/role` — rôle et statut.
 *
 * Le serveur refuse l'escalade (`tenant_admin` ne peut pas fabriquer un
 * `tenant_admin` ni un `super_admin`) et le changement de son propre rôle. On ne
 * rejoue pas ces règles ici.
 */
export function useUpdateMemberRole() {
  const invalider = useInvalidateTeam();
  return useMutation({
    mutationFn: (args: { id: string; role?: TeamRole; status?: TeamStatus }) =>
      api.patch<TeamMember>(`/v1/users/${args.id}/role`, {
        ...(args.role ? { role: args.role } : {}),
        ...(args.status ? { status: args.status } : {}),
      }),
    onSuccess: invalider,
  });
}

/** `PATCH /v1/users/:id` — profil (nom, téléphone, langue). */
export function useUpdateMemberProfile() {
  const invalider = useInvalidateTeam();
  return useMutation({
    mutationFn: (args: {
      id: string;
      full_name?: string;
      phone?: string | null;
      locale?: string;
    }) => {
      const { id, ...corps } = args;
      return api.patch<TeamMember>(`/v1/users/${id}`, corps);
    },
    onSuccess: invalider,
  });
}

/**
 * `PATCH /v1/brokers/:id` — les données propres au courtier.
 *
 * C'est ce qui ferme `D-NEW-WEB-BROKERS-READ-ONLY` : le backend acceptait déjà ces
 * mutations, aucun écran ne les exposait.
 */
export function useUpdateBrokerProfile() {
  const invalider = useInvalidateTeam();
  return useMutation({
    mutationFn: (args: {
      id: string;
      license_number?: string | null;
      license_expiry?: string | null;
      specialties?: string[];
      bio?: string | null;
    }) => {
      const { id, ...corps } = args;
      return api.patch<TeamMember>(`/v1/brokers/${id}`, corps);
    },
    onSuccess: invalider,
  });
}

/**
 * `DELETE /v1/users/:id` — retrait de l’appartenance au cabinet sélectionné.
 *
 * Le serveur fait un soft-delete : `deleted_at` + `status = 'disabled'`. Rien n'est
 * effacé, et le libellé de l'interface dit « désactiver » plutôt que « supprimer »
 * pour ne pas promettre une destruction qui n'a pas lieu.
 */
export function useDeactivateMember() {
  const invalider = useInvalidateTeam();
  return useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/v1/users/${id}`),
    onSuccess: invalider,
  });
}
